import React, { useEffect, useState } from 'react';

/* -------- packages -------- */
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { 
  Form, Row, Col, FormGroup, Label, Input, 
  Button, FormFeedback, Card, CardBody, CardTitle, Table 
} from 'reactstrap';
import Collapsible from 'react-collapsible';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
/* -------- ------- -------- */


/* -------- intern functions -------- */
import { hasRight } from '../../../utils/userTools';
/* -------- ------- -------- */

const DynamicRightsForm = ({ keys, initialValues, onSubmit, error }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state) => state.Login.user);

  const [parentCheckedState, setParentCheckedState] = useState({});

  const validationSchema = Yup.object().shape({
    lib: Yup.string()
      .required(t('REQUIRED'))
      .matches(/^[a-zA-ZÀ-ÿ\- ']+$/, t('LETTER'))
  });

  const getFormattedInitialValues = (initValues) => {
    const formattedValues = {};
    keys.forEach(key => {
      if (key.subItems?.length > 0) {
        key.subItems.forEach(subItem => {
          formattedValues[subItem.rightName] = {
            create: initValues?.rights?.includes(`${subItem.rightName}_c`) || false,
            read: initValues?.rights?.includes(`${subItem.rightName}_r`) || false,
            edit: initValues?.rights?.includes(`${subItem.rightName}_w`) || false,
            delete: initValues?.rights?.includes(`${subItem.rightName}_d`) || false,
          };
        });
      } else {
        formattedValues[key.rightName] = {
          create: initValues?.rights?.includes(`${key.rightName}_c`) || false,
          read: initValues?.rights?.includes(`${key.rightName}_r`) || false,
          edit: initValues?.rights?.includes(`${key.rightName}_w`) || false,
          delete: initValues?.rights?.includes(`${key.rightName}_d`) || false,
        };
      }
    });
    return formattedValues;
  };

  const formik = useFormik({
    initialValues: {
      lib: initialValues.lib || '',
      rights: getFormattedInitialValues(initialValues),
    },
    validationSchema,
    onSubmit: values => {
      const formattedRights = [];
      Object.entries(values.rights).forEach(([rightName, right]) => {
        
        let specialRight = true;
        ['create', 'read', 'edit', 'delete'].forEach(action => {
          if (right[action] != null) {
            specialRight = false;
            if(right[action] == true) {
              formattedRights.push(`${rightName}_${action.charAt(0)}`);
            }
          }
        });
        if(specialRight) {
          if(right) {
            formattedRights.push(rightName);
          }
        }
      });

      onSubmit({ ...values, rights: formattedRights });
    },
  });

  useEffect(() => {
    const newState = {};
    keys.forEach(item => {
      if (item.subItems?.length > 0) {
        ['create', 'read', 'edit', 'delete'].forEach(action => {
          newState[`${item.rightName}_${action}`] = item.subItems.every(subItem => formik.values.rights[subItem.rightName]?.[action]);
        });
      }
    });
    setParentCheckedState(newState);
  }, [formik.values.rights, keys]);

  const renderSpecialRights = (specialRights, item) => {
    if(specialRights && specialRights.length > 0) {
      return specialRights.map((right) => (
        <div key={right} className="d-flex align-items-center">
          <Label className="mb-0">{t(right)}:</Label>
          <FormGroup switch className="ms-2 mb-0">
            <Input
              type="switch"
              role="switch"
              checked={formik.values.rights[right] || false}
              onChange={(e) => formik.setFieldValue(`rights.${right}`, e.target.checked)} 
            />
          </FormGroup>
        </div>
      ));
    } else {
      return null;
    }
  };

  const handleCheckboxClick = (item, action) => {
    if (item.subItems?.length > 0) {
      const allChecked = item.subItems.every(subItem => formik.values.rights[subItem.rightName]?.[action]);
      // Si tous les sous-items sont sélectionnés, désélectionner "Tout", sinon sélectionner "Tout"
      item.subItems.forEach(subItem => {
        formik.setFieldValue(`rights.${subItem.rightName}.${action}`, !allChecked);
      });
    } else {
      formik.setFieldValue(`rights.${item.rightName}.${action}`, !formik.values.rights[item.rightName][action]);
    }
  };
  
  const renderRightsRow = (rightName, label) => (
    <tr key={rightName}>
      <td>{t(label)}</td>
      {['create', 'read', 'edit', 'delete'].map(action => (
        <td key={`${action}-${rightName}`}>
          <Input
            type="checkbox"
            className='rights-checkbox'
            id={`${action}-${rightName}`}
            name={`rights.${rightName}.${action}`}
            onClick={() => formik.setFieldValue(`rights.${rightName}.${action}`, !formik.values.rights[rightName][action])}
            onChange={() => {}}
            checked={formik.values.rights[rightName]?.[action] || false}
            disabled={!hasRight(rightName, action.charAt(0), user)}
          />
        </td>
      ))}
    </tr>
  );
  
  const renderMenuItem = (item) => {
    const allRightsChecked = item.subItems?.every(subItem =>
      ['create', 'read', 'edit', 'delete'].every(action =>
        formik.values.rights[subItem.rightName]?.[action]
      )
    );
    
    return (
      <Card key={item.rightName || item.label} className="card-container">
        {item.subItems?.length > 0 ? (
          <Collapsible
            trigger={
              <CardBody className="card-title-row card-subitem">
                <CardTitle tag="h5" className="label-margin-zero">{t(item.label)}</CardTitle>
                <div className="quickCheckboxContainer withLabel">
                <Label className="label-margin-zero checkboxLabel">{t('all')}:</Label>

                  <div className='quickCheckboxRow'>
                  {['create', 'read', 'edit', 'delete'].map(action => (
                    <FormGroup key={action}>
                      <Label className="label-margin-zero">{t(action)}</Label>
                      <Input
                        type="checkbox"
                        className='rights-checkbox'
                        id={`${action}-${item.rightName}`}
                        name={`rights.${item.rightName}.${action}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCheckboxClick(item, action);
                        }}
                        onChange={() => {}}
                        checked={parentCheckedState[`${item.rightName}_${action}`] || false}
                        disabled={!allRightsChecked}
                      />
                    </FormGroup>
                  ))}

                  </div>
                </div>
              </CardBody>
            }
          >
            <div className="p-3">
              <Table bordered className="table-rights">
                <thead>
                  <tr>
                    <th className='customDynamicBg tableHead'></th>
                    <th className='customDynamicBg tableHead'>{t('create')}</th>
                    <th className='customDynamicBg tableHead'>{t('read')}</th>
                    <th className='customDynamicBg tableHead'>{t('edit')}</th>
                    <th className='customDynamicBg tableHead'>{t('delete')}</th>
                  </tr>
                </thead>
                <tbody>
                  {item.subItems.map(subItem => renderRightsRow(subItem.rightName, subItem.label))}
                </tbody>
              </Table>
              {renderSpecialRights(item.specialRights, item)}
            </div>
          </Collapsible>
        ) : (
          <CardBody className="card-title-row">
            <CardTitle tag="h5" className="label-margin-zero">{t(item.label)}</CardTitle>
            <div className="quickCheckboxContainer">

            <div className="quickCheckboxRow">
              {['create', 'read', 'edit', 'delete'].map(action => (
                <FormGroup key={action}>
                  <Label className="label-margin-zero">{t(action)}</Label>
                  <Input
                    type="checkbox"
                    id={`${action}-${item.rightName}`}
                    className='rights-checkbox'
                    name={`rights.${item.rightName}.${action}`}
                    onClick={() => handleCheckboxClick(item, action)}
                    onChange={() => {}}
                    checked={formik.values.rights[item.rightName]?.[action] || false}
                    disabled={!hasRight(item.rightName, action.charAt(0), user) || !allRightsChecked} 
                    />
                </FormGroup>
              ))}
              {item.specialRights && renderSpecialRights(item.specialRights, item)}
              </div>
            </div>
          </CardBody>
        )}
      </Card>
    );
  };

  return (
    <Form onSubmit={formik.handleSubmit} className='newGroupForm'>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <FormGroup>
                <Label>{t('lib')}<span className="text-danger">*</span></Label>
                <Input
                  id="lib"
                  name="lib"
                  value={formik.values.lib}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.lib && !!formik.errors.lib}
                  />
                <FormFeedback>{formik.errors.lib}</FormFeedback>
              </FormGroup>
              <div>
                {keys.map(item => renderMenuItem(item))}
              </div>
              <span className='card-footer'>
                <Button id={'projGroup_cancelCreate'} className='btn-secondary ' type="button" onClick={() => navigate(-1)}>{t('cancel')}</Button>
                <Button id={'projGroup_submitCreate'} className='btn-primary' type="submit">{t('submit')}</Button>
              </span>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};

export default DynamicRightsForm;
